import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, Platform, ActivityIndicator, Modal, Alert } from 'react-native';
import { Picker } from '@react-native-picker/picker';
import WebScrollView from '../components/WebScrollView';
import { Receipt, Plus, X, Calendar, DollarSign, TrendingUp, Paperclip, Trash2, ExternalLink, Download, Clock, CheckCircle2 } from '../lib/icons';
import { supabase } from '../lib/supabase';
import { ExpenseFileUpload } from '../components/ExpenseFileUpload';
import { formatDateOnly, formatDateForDisplay } from '../lib/dateUtils';
import { getExpenseContextLabel } from '../lib/adminExpenses';
import { useActiveHome } from '../contexts/ActiveHomeContext';

interface Expense {
  id: string;
  home_id: string;
  title: string;
  amount: number;
  date: string;
  category: string;
  description: string;
  receipt_url: string | null;
  created_at: string;
}

interface MaintenanceRecord {
  id: string;
  item_id: string;
  service_date: string;
  service_type: string;
  provider: string;
  cost: number;
  description: string;
  inventory_items: {
    id: string;
    name: string;
    home_id: string;
  };
}

interface InventoryPurchase {
  id: string;
  home_id: string;
  name: string;
  category: string;
  purchase_date: string;
  purchase_price: number;
  model_number: string | null;
  serial_number: string | null;
}

interface ServiceRequestExpense {
  id: string;
  service_request_id: string | null;
  home_id?: string | null;
  visit_id?: string | null;
  parent_expense_id: string | null;
  amount: number;
  description: string | null;
  category: string | null;
  expense_date: string | null;
  created_at: string;
  is_paid: boolean;
  billable_to_homeowner: boolean;
  invoice_id: string | null;
  invoice_requested: boolean;
  service_request_title: string | null;
  visit_context_label: string | null;
}

interface Home {
  id: string;
  address: string;
  city: string;
  state: string;
}

interface Invoice {
  id: string;
  homeowner_id: string;
  home_id: string;
  stripe_invoice_id: string;
  stripe_customer_id: string;
  total_amount: number;
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  stripe_hosted_invoice_url: string | null;
  stripe_invoice_pdf: string | null;
  due_date: string | null;
  paid_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// Create a unified type for displaying in the list
type ExpenseRecord = 
  | (Expense & { type: 'expense' })
  | (MaintenanceRecord & { type: 'maintenance'; amount: number; item_name: string })
  | (InventoryPurchase & { type: 'purchase'; amount: number })
  | (ServiceRequestExpense & { type: 'service_request'; date: string })

const expenseCategories = [
  'Maintenance',
  'Repairs',
  'Improvements',
  'Utilities',
  'Insurance',
  'Professional Services',
  'Supplies',
  'Other'
];

// Custom Dropdown Picker Component
const DropdownPicker = ({ 
  visible, 
  onClose, 
  options, 
  selectedValue, 
  onSelect 
}: { 
  visible: boolean; 
  onClose: () => void; 
  options: Array<{ value: string; label: string }>; 
  selectedValue: string; 
  onSelect: (value: string) => void;
}) => {
  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={pickerStyles.pickerModalOverlay}>
        <Pressable style={pickerStyles.pickerModalBackdrop} onPress={onClose} />
        <View style={pickerStyles.pickerModalContent}>
          <View style={pickerStyles.pickerHeader}>
            <Text style={pickerStyles.pickerHeaderText}>Select Option</Text>
            <Pressable onPress={onClose}>
              <X size={20} color="#6b7280" />
            </Pressable>
          </View>
          <WebScrollView style={pickerStyles.pickerOptions}>
            {options.map((option) => (
              <Pressable
                key={option.value}
                onPress={() => {
                  onSelect(option.value);
                  onClose();
                }}
                style={[
                  pickerStyles.pickerOption,
                  selectedValue === option.value && pickerStyles.pickerOptionSelected
                ]}
              >
                <Text style={[
                  pickerStyles.pickerOptionText,
                  selectedValue === option.value && pickerStyles.pickerOptionTextSelected
                ]}>
                  {option.label}
                </Text>
              </Pressable>
            ))}
          </WebScrollView>
        </View>
      </View>
    </Modal>
  );
};

const Expenses = () => {
  const { homes: accessibleHomes, activeHome, setActiveHomeById, loading: homesLoading } = useActiveHome();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [homes, setHomes] = useState<Home[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [maintenanceRecords, setMaintenanceRecords] = useState<MaintenanceRecord[]>([]);
  const [inventoryPurchases, setInventoryPurchases] = useState<InventoryPurchase[]>([]);
  const [serviceRequestExpenses, setServiceRequestExpenses] = useState<ServiceRequestExpense[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [selectedHome, setSelectedHome] = useState<string>('');
  const [error, setError] = useState('');
  const [showHomePicker, setShowHomePicker] = useState(false);
  const [newExpenseId, setNewExpenseId] = useState<string | null>(null);
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null);
  const [expenseAttachmentCounts, setExpenseAttachmentCounts] = useState<Record<string, number>>({});
  const [formData, setFormData] = useState({
    home_id: '',
    title: '',
    amount: '',
    date: formatDateOnly(new Date()),
    category: 'Maintenance',
    description: '',
    receipt_url: ''
  });

  useEffect(() => {
    setHomes(accessibleHomes as Home[]);
    if (activeHome?.id) {
      setSelectedHome(activeHome.id);
      setFormData((prev) => ({ ...prev, home_id: activeHome.id }));
    } else if (!homesLoading) {
      setSelectedHome('');
      setLoading(false);
    }
  }, [accessibleHomes, activeHome, homesLoading]);

  useEffect(() => {
    if (selectedHome) {
      fetchExpenses();
      fetchAttachmentCounts();
      fetchInvoices();
    }
  }, [selectedHome]);

  const fetchAttachmentCounts = async () => {
    if (!selectedHome) return;
    
    try {
      // Get all expense IDs for the selected home
      const expenseIds = expenses.map(e => e.id);
      if (expenseIds.length === 0) return;

      const { data, error } = await supabase
        .from('expense_attachments')
        .select('expense_id')
        .in('expense_id', expenseIds);

      if (error) throw error;

      // Count attachments per expense
      const counts: Record<string, number> = {};
      data?.forEach(attachment => {
        counts[attachment.expense_id] = (counts[attachment.expense_id] || 0) + 1;
      });

      setExpenseAttachmentCounts(counts);
    } catch (error) {
      console.error('Error fetching attachment counts:', error);
    }
  };

  const fetchExpenses = async () => {
    if (!selectedHome) {
      setExpenses([]);
      setMaintenanceRecords([]);
      setInventoryPurchases([]);
      setServiceRequestExpenses([]);
      setLoading(false);
      return;
    }

    try {
      // Fetch regular expenses
      const { data: expenseData, error: expenseError } = await supabase
        .from('expenses')
        .select('*')
        .eq('home_id', selectedHome)
        .order('date', { ascending: false });

      if (expenseError) throw expenseError;

      // Fetch maintenance records for items in this home
      const { data: maintenanceData, error: maintenanceError } = await supabase
        .from('maintenance_records')
        .select(`
          *,
          inventory_items!inner (
            id,
            name,
            home_id
          )
        `)
        .eq('inventory_items.home_id', selectedHome)
        .order('service_date', { ascending: false });

      if (maintenanceError) throw maintenanceError;

      // Fetch inventory purchases (items with purchase date and price)
      const { data: inventoryData, error: inventoryError } = await supabase
        .from('inventory_items')
        .select('id, home_id, name, category, purchase_date, purchase_price, model_number, serial_number')
        .eq('home_id', selectedHome)
        .not('purchase_date', 'is', null)
        .not('purchase_price', 'is', null)
        .order('purchase_date', { ascending: false });

      if (inventoryError) throw inventoryError;

      // Fetch service request parent expenses for this home.
      // Parent-only keeps totals aligned and avoids line-item double counting.
      const { data: serviceRequests, error: serviceRequestsError } = await supabase
        .from('service_requests')
        .select('id, title')
        .eq('home_id', selectedHome);

      if (serviceRequestsError) throw serviceRequestsError;

      let mappedServiceRequestExpenses: ServiceRequestExpense[] = [];
      const serviceRequestIds = (serviceRequests || []).map((request) => request.id);

      const serviceRequestTitleById = new Map(
        (serviceRequests || []).map((request) => [request.id, request.title || null])
      );

      const { data: visitData, error: visitsError } = await supabase
        .from('visits')
        .select('id, visit_type, scheduled_date, internal_name')
        .eq('home_id', selectedHome);

      if (visitsError) throw visitsError;

      const visitById = new Map((visitData || []).map((visit) => [visit.id, visit]));

      const operationalExpenseSelect =
        'id, service_request_id, home_id, visit_id, parent_expense_id, amount, description, category, expense_date, created_at, is_paid, billable_to_homeowner, invoice_id, invoice_requested';

      const mapOperationalExpense = (expense: Omit<ServiceRequestExpense, 'service_request_title' | 'visit_context_label'>) => {
        const visit = expense.visit_id ? visitById.get(expense.visit_id) : null;
        const contextLabel = getExpenseContextLabel({
          service_request_id: expense.service_request_id,
          visit_id: expense.visit_id,
          visit,
        });

        if (expense.service_request_id) {
          return {
            ...expense,
            service_request_title: serviceRequestTitleById.get(expense.service_request_id) || 'Service Request',
            visit_context_label: null,
          };
        }

        return {
          ...expense,
          service_request_title: expense.description || contextLabel,
          visit_context_label: expense.description ? contextLabel : null,
        };
      };

      const { data: standaloneExpenseData, error: standaloneExpensesError } = await supabase
        .from('service_request_expenses')
        .select(operationalExpenseSelect)
        .eq('home_id', selectedHome)
        .is('service_request_id', null)
        .is('parent_expense_id', null)
        .eq('billable_to_homeowner', true)
        .order('created_at', { ascending: false });

      if (standaloneExpensesError) throw standaloneExpensesError;

      let linkedExpenseData: any[] = [];
      if (serviceRequestIds.length > 0) {
        const { data: serviceRequestExpenseData, error: serviceRequestExpensesError } = await supabase
          .from('service_request_expenses')
          .select(operationalExpenseSelect)
          .in('service_request_id', serviceRequestIds)
          .is('parent_expense_id', null)
          .eq('billable_to_homeowner', true)
          .order('created_at', { ascending: false });

        if (serviceRequestExpensesError) throw serviceRequestExpensesError;
        linkedExpenseData = serviceRequestExpenseData || [];
      }

      mappedServiceRequestExpenses = [...linkedExpenseData, ...(standaloneExpenseData || [])].map(mapOperationalExpense);

      setExpenses(expenseData || []);
      setMaintenanceRecords(maintenanceData || []);
      setInventoryPurchases(inventoryData || []);
      setServiceRequestExpenses(mappedServiceRequestExpenses);
      
      // Fetch attachment counts after expenses are loaded
      if (expenseData && expenseData.length > 0) {
        const expenseIds = expenseData.map(e => e.id);
        const { data: attachmentData } = await supabase
          .from('expense_attachments')
          .select('expense_id')
          .in('expense_id', expenseIds);

        if (attachmentData) {
          const counts: Record<string, number> = {};
          attachmentData.forEach(attachment => {
            counts[attachment.expense_id] = (counts[attachment.expense_id] || 0) + 1;
          });
          setExpenseAttachmentCounts(counts);
        }
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
      setError('Failed to load expenses. Please try again later.');
      setExpenses([]);
      setMaintenanceRecords([]);
      setInventoryPurchases([]);
      setServiceRequestExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    if (!selectedHome) {
      setInvoices([]);
      return;
    }

    try {
      setLoadingInvoices(true);
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('home_id', selectedHome)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setInvoices(data || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleViewInvoice = (invoiceUrl: string) => {
    if (Platform.OS === 'web') {
      window.open(invoiceUrl, '_blank');
    }
  };

  const getInvoiceStatusColor = (status: Invoice['status']) => {
    switch (status) {
      case 'paid':
        return { backgroundColor: '#dcfce7', color: '#16a34a' };
      case 'open':
        return { backgroundColor: '#fef3c7', color: '#d97706' };
      case 'void':
      case 'uncollectible':
        return { backgroundColor: '#f3f4f6', color: '#6b7280' };
      default:
        return { backgroundColor: '#fee2e2', color: '#dc2626' };
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const { data, error } = await supabase
        .from('expenses')
        .insert([{
          ...formData,
          amount: parseFloat(formData.amount),
          home_id: selectedHome
        }])
        .select()
        .single();

      if (error) throw error;

      // Set the new expense ID so file upload can be shown
      setNewExpenseId(data.id);
      
      // Don't close modal yet - allow user to add files
      // They can close manually or we'll close after a delay
      await fetchExpenses();
    } catch (error: any) {
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setIsFormOpen(false);
    setNewExpenseId(null);
    setEditingExpenseId(null);
    setFormData({
      home_id: selectedHome,
      title: '',
      amount: '',
      date: formatDateOnly(new Date()),
      category: 'Maintenance',
      description: '',
      receipt_url: ''
    });
  };

  const handleDeleteExpense = async (expenseId: string) => {
    Alert.alert(
      'Delete Expense',
      'Are you sure you want to delete this expense? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              // First, delete all attachments for this expense
              const { data: attachments } = await supabase
                .from('expense_attachments')
                .select('file_url')
                .eq('expense_id', expenseId);

              if (attachments && attachments.length > 0) {
                // Delete files from storage
                const filePaths = attachments.map(a => a.file_url);
                await supabase.storage
                  .from('expense-attachments')
                  .remove(filePaths);
              }

              // Delete the expense (attachments will be cascade deleted)
              const { error } = await supabase
                .from('expenses')
                .delete()
                .eq('id', expenseId);

              if (error) throw error;

              // Refresh the expenses list
              await fetchExpenses();
            } catch (error: any) {
              console.error('Error deleting expense:', error);
              Alert.alert('Error', error.message || 'Failed to delete expense. Please try again.');
            }
          }
        }
      ]
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Function to combine and sort all types of records
  const getCombinedExpenses = (): ExpenseRecord[] => {
    const maintenanceExpenses: ExpenseRecord[] = maintenanceRecords.map(record => ({
      ...record,
      type: 'maintenance' as const,
      amount: record.cost || 0,
      item_name: record.inventory_items.name,
    }));

    const regularExpenses: ExpenseRecord[] = expenses.map(expense => ({
      ...expense,
      type: 'expense' as const
    }));

    const purchaseExpenses: ExpenseRecord[] = inventoryPurchases.map(purchase => ({
      ...purchase,
      type: 'purchase' as const,
      amount: purchase.purchase_price || 0,
    }));

    const serviceExpenses: ExpenseRecord[] = serviceRequestExpenses.map(expense => ({
      ...expense,
      type: 'service_request' as const,
      date: expense.expense_date || expense.created_at,
    }));

    return [...maintenanceExpenses, ...regularExpenses, ...purchaseExpenses, ...serviceExpenses]
      .sort((a, b) => {
        let dateA: Date;
        let dateB: Date;
        
        if (a.type === 'expense') {
          dateA = new Date(a.date);
        } else if (a.type === 'maintenance') {
          dateA = new Date(a.service_date);
        } else if (a.type === 'service_request') {
          dateA = new Date(a.date);
        } else {
          dateA = new Date(a.purchase_date);
        }
        
        if (b.type === 'expense') {
          dateB = new Date(b.date);
        } else if (b.type === 'maintenance') {
          dateB = new Date(b.service_date);
        } else if (b.type === 'service_request') {
          dateB = new Date(b.date);
        } else {
          dateB = new Date(b.purchase_date);
        }
        
        return dateB.getTime() - dateA.getTime();
      });
  };

  const calculateTotal = () => {
    const expenseTotal = expenses.reduce((sum, expense) => sum + expense.amount, 0);
    const maintenanceTotal = maintenanceRecords.reduce((sum, record) => sum + (record.cost || 0), 0);
    const purchaseTotal = inventoryPurchases.reduce((sum, purchase) => sum + (purchase.purchase_price || 0), 0);
    const serviceRequestTotal = serviceRequestExpenses.reduce((sum, expense) => sum + expense.amount, 0);
    return expenseTotal + maintenanceTotal + purchaseTotal + serviceRequestTotal;
  };

  const calculateYearlyStats = () => {
    const currentYear = new Date().getFullYear();
    const yearExpenses = expenses.filter(expense => 
      new Date(expense.date).getFullYear() === currentYear
    );
    const yearMaintenance = maintenanceRecords.filter(record => 
      new Date(record.service_date).getFullYear() === currentYear
    );
    const yearPurchases = inventoryPurchases.filter(purchase => 
      new Date(purchase.purchase_date).getFullYear() === currentYear
    );
    const yearServiceRequestExpenses = serviceRequestExpenses.filter(expense =>
      new Date(expense.expense_date || expense.created_at).getFullYear() === currentYear
    );

    const expenseTotal = yearExpenses.reduce((sum, expense) => sum + expense.amount, 0);
    const maintenanceTotal = yearMaintenance.reduce((sum, record) => sum + (record.cost || 0), 0);
    const purchaseTotal = yearPurchases.reduce((sum, purchase) => sum + (purchase.purchase_price || 0), 0);
    const serviceRequestTotal = yearServiceRequestExpenses.reduce((sum, expense) => sum + expense.amount, 0);
    const total = expenseTotal + maintenanceTotal + purchaseTotal + serviceRequestTotal;
    
    const byCategory = yearExpenses.reduce((acc, expense) => {
      acc[expense.category] = (acc[expense.category] || 0) + expense.amount;
      return acc;
    }, {} as Record<string, number>);

    // Add maintenance as a category
    if (maintenanceTotal > 0) {
      byCategory['Maintenance (Items)'] = maintenanceTotal;
    }

    // Add purchases as a category
    if (purchaseTotal > 0) {
      byCategory['Equipment Purchases'] = purchaseTotal;
    }

    yearServiceRequestExpenses.forEach((expense) => {
      const category = expense.category || 'Service Request';
      byCategory[category] = (byCategory[category] || 0) + expense.amount;
    });

    const topCategory = Object.entries(byCategory)
      .sort(([,a], [,b]) => b - a)[0];

    return {
      total,
      topCategory: topCategory ? {
        name: topCategory[0],
        amount: topCategory[1]
      } : null
    };
  };

  const yearlyStats = calculateYearlyStats();

  const getCategoryBadgeStyle = (type: string) => {
    if (type === 'maintenance') {
      return { backgroundColor: '#dbeafe', color: '#1e40af' };
    } else if (type === 'purchase') {
      return { backgroundColor: '#d1fae5', color: '#065f46' };
    } else if (type === 'service_request') {
      return { backgroundColor: '#fef3c7', color: '#92400e' };
    }
    return { backgroundColor: '#f3f4f6', color: '#111827' };
  };

  return (
    <WebScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>Expenses</Text>
          <Text style={styles.subtitle}>Track your home maintenance expenses</Text>
        </View>
        <Pressable
          onPress={() => setIsFormOpen(true)}
          style={styles.addButton}
        >
          <Plus size={16} color="#ffffff" />
          <Text style={styles.addButtonText}>Add Expense</Text>
        </Pressable>
      </View>

      {!loading && (expenses.length > 0 || maintenanceRecords.length > 0 || inventoryPurchases.length > 0 || serviceRequestExpenses.length > 0) && (
        <View style={styles.statsCard}>
          <View style={styles.statsCardHeader}>
            <TrendingUp size={24} color="#000000" />
            <Text style={styles.statsCardTitle}>This Year</Text>
          </View>
          <View style={styles.statsCardContent}>
            <View style={styles.statItem}>
              <Text style={styles.statLabel}>Total Spent</Text>
              <Text style={styles.statValue}>{formatCurrency(yearlyStats.total)}</Text>
            </View>
              {yearlyStats.topCategory && (
              <View style={styles.statItem}>
                <Text style={styles.statLabel}>Top Category</Text>
                <View style={styles.statCategoryRow}>
                  <Text style={styles.statCategoryName}>{yearlyStats.topCategory.name}</Text>
                  <Text style={styles.statCategoryAmount}>{formatCurrency(yearlyStats.topCategory.amount)}</Text>
                </View>
              </View>
            )}
          </View>
        </View>
      )}

      {/* Invoices Section */}
      {invoices.length > 0 && (
        <View style={styles.card}>
          <View style={styles.cardContent}>
            <View style={styles.cardHeader}>
              <View style={styles.cardHeaderLeft}>
                <DollarSign size={24} color="#000000" />
                <Text style={styles.cardTitle}>Invoices</Text>
              </View>
            </View>

            {loadingInvoices ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="small" color="#9ca3af" />
              </View>
            ) : (
              <View style={styles.invoicesList}>
                {invoices.map((invoice) => {
                  const statusStyle = getInvoiceStatusColor(invoice.status);
                  const isOverdue = invoice.status === 'open' && invoice.due_date && new Date(invoice.due_date) < new Date();
                  const linkedTitles = [
                    ...new Set(
                      serviceRequestExpenses
                        .filter((exp) => exp.invoice_id === invoice.id && exp.service_request_title)
                        .map((exp) => exp.service_request_title as string)
                    )
                  ];

                  return (
                    <View key={invoice.id} style={styles.invoiceCard}>
                      <View style={styles.invoiceCardHeader}>
                        <View style={styles.invoiceCardHeaderLeft}>
                          <View style={styles.invoiceHeaderTopRow}>
                            <View style={styles.invoiceNumberContainer}>
                              <DollarSign size={18} color="#6b7280" />
                              <Text style={styles.invoiceNumber}>
                                Invoice #{invoice.stripe_invoice_id.substring(3, 11).toUpperCase()}
                              </Text>
                            </View>
                            <View style={styles.invoiceAmountContainer}>
                              <Text style={styles.invoiceAmount}>
                                {formatCurrency(invoice.total_amount)}
                              </Text>
                            </View>
                          </View>
                          <View style={styles.invoiceBadgesContainer}>
                            <View style={[styles.invoiceStatusBadge, { backgroundColor: statusStyle.backgroundColor }]}>
                              <Text style={[styles.invoiceStatusText, { color: statusStyle.color }]}>
                                {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                              </Text>
                            </View>
                            {isOverdue && (
                              <View style={[styles.invoiceStatusBadge, styles.invoiceOverdueBadge]}>
                                <Text style={[styles.invoiceStatusText, styles.invoiceOverdueText]}>
                                  Overdue
                                </Text>
                              </View>
                            )}
                          </View>
                        </View>
                      </View>

                      {linkedTitles.length > 0 && (
                        <View style={styles.invoiceLineItems}>
                          <Text style={styles.invoiceLineItemsLabel}>Covers</Text>
                          {linkedTitles.map((title, i) => (
                            <Text key={i} style={styles.invoiceLineItemText}>• {title}</Text>
                          ))}
                        </View>
                      )}

                      <View style={styles.invoiceDivider} />

                      <View style={styles.invoiceDetails}>
                        <View style={styles.invoiceDetailRow}>
                          <View style={styles.invoiceDetailIconContainer}>
                            <Calendar size={14} color="#6b7280" />
                            <Text style={styles.invoiceDetailLabel}>Created</Text>
                          </View>
                          <Text style={styles.invoiceDetailValue}>
                            {formatDateForDisplay(invoice.created_at)}
                          </Text>
                        </View>
                        {invoice.due_date && (
                          <View style={styles.invoiceDetailRow}>
                            <View style={styles.invoiceDetailIconContainer}>
                              <Clock size={14} color={isOverdue ? '#dc2626' : '#6b7280'} />
                              <Text style={[styles.invoiceDetailLabel, isOverdue && styles.invoiceDetailLabelOverdue]}>
                                Due Date
                              </Text>
                            </View>
                            <Text style={[
                              styles.invoiceDetailValue,
                              isOverdue && styles.invoiceDetailValueOverdue
                            ]}>
                              {formatDateForDisplay(invoice.due_date)}
                            </Text>
                          </View>
                        )}
                        {invoice.paid_at && (
                          <View style={styles.invoiceDetailRow}>
                            <View style={styles.invoiceDetailIconContainer}>
                              <CheckCircle2 size={14} color="#16a34a" />
                              <Text style={styles.invoiceDetailLabel}>Paid On</Text>
                            </View>
                            <Text style={styles.invoiceDetailValuePaid}>
                              {formatDateForDisplay(invoice.paid_at)}
                            </Text>
                          </View>
                        )}
                      </View>

                      {invoice.status === 'open' && invoice.stripe_hosted_invoice_url && (
                        <Pressable
                          onPress={() => handleViewInvoice(invoice.stripe_hosted_invoice_url!)}
                          style={styles.invoicePayButton}
                        >
                          <Text style={styles.invoicePayButtonText}>View & Pay Invoice</Text>
                          <ExternalLink size={18} color="#ffffff" />
                        </Pressable>
                      )}

                      {invoice.status === 'paid' && invoice.stripe_invoice_pdf && (
                        <Pressable
                          onPress={() => handleViewInvoice(invoice.stripe_invoice_pdf!)}
                          style={styles.invoiceDownloadButton}
                        >
                          <Download size={18} color="#111827" />
                          <Text style={styles.invoiceDownloadButtonText}>Download PDF</Text>
                        </Pressable>
                      )}
                    </View>
                  );
                })}
              </View>
            )}
          </View>
        </View>
      )}

      <View style={styles.card}>
        <View style={styles.cardContent}>
          <View style={styles.cardHeader}>
            <View style={styles.cardHeaderLeft}>
              <Receipt size={24} color="#000000" />
              <Text style={styles.cardTitle}>Expense History</Text>
            </View>
          </View>
          <View style={styles.homePickerContainer}>
            <Pressable
              onPress={() => setShowHomePicker(true)}
              style={styles.homePicker}
            >
              <Text style={styles.homePickerText} numberOfLines={1}>
                {homes.find(h => h.id === selectedHome)?.address || 'Select Home'}
              </Text>
            </Pressable>
          </View>

          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#9ca3af" />
            </View>
          ) : expenses.length === 0 && maintenanceRecords.length === 0 && inventoryPurchases.length === 0 && serviceRequestExpenses.length === 0 ? (
            <View style={styles.emptyStateContainer}>
              <Receipt size={32} color="#9ca3af" />
              <View style={styles.emptyStateTextContainer}>
                <Text style={styles.emptyStateTitle}>No Expenses</Text>
                <Text style={styles.emptyStateDescription}>
                  Start tracking your home maintenance expenses
                </Text>
              </View>
            </View>
          ) : (
            <View style={styles.expensesCardList}>
              {getCombinedExpenses().map((record) => {
                const badgeStyle = getCategoryBadgeStyle(record.type);
                const recordDate = record.type === 'expense' ? record.date : 
                                  record.type === 'maintenance' ? record.service_date : 
                                  record.type === 'service_request' ? record.date :
                                  record.purchase_date;
                const linkedInvoice = record.type === 'service_request' && record.invoice_id
                  ? invoices.find((invoice) => invoice.id === record.invoice_id)
                  : null;
                const showServiceRequestDescription =
                  record.type === 'service_request' &&
                  record.description &&
                  record.description !== record.service_request_title;
                return (
                  <View key={`${record.type}-${record.id}`} style={styles.expenseCard}>
                    <View style={styles.expenseCardHeader}>
                      <View style={styles.expenseCardHeaderLeft}>
                        <Text style={styles.expenseCardTitle}>
                          {record.type === 'expense' ? record.title : 
                           record.type === 'maintenance' ? `${record.service_type} - ${record.item_name}` :
                           record.type === 'service_request' ? (record.service_request_title || 'Service Request') :
                           `Purchase - ${record.name}`}
                        </Text>
                        <View style={styles.expenseCardMeta}>
                          <Text style={styles.expenseCardDate}>
                            {formatDateForDisplay(recordDate)}
                          </Text>
                          {record.type === 'service_request' && record.visit_context_label && (
                            <Text style={styles.expenseCardContext}>{record.visit_context_label}</Text>
                          )}
                          <View style={[styles.categoryBadge, { backgroundColor: badgeStyle.backgroundColor }]}>
                            <Text style={[styles.categoryBadgeText, { color: badgeStyle.color }]}>
                              {record.type === 'expense' ? record.category : 
                               record.type === 'maintenance' ? 'Maintenance' : 
                               record.type === 'service_request' ? (record.category || 'Service Request') :
                               'Purchase'}
                            </Text>
                          </View>
                          {record.type === 'service_request' && !record.is_paid && (
                            <View style={styles.unpaidBadge}>
                              <Text style={styles.unpaidBadgeText}>Unpaid</Text>
                            </View>
                          )}
                        </View>
                      </View>
                      <View style={styles.expenseCardHeaderRight}>
                        <Text style={styles.expenseCardAmount}>
                          {formatCurrency(record.amount)}
                        </Text>
                        {record.type === 'expense' && (
                          <View style={styles.expenseCardActions}>
                            <Pressable
                              onPress={() => {
                                setEditingExpenseId(record.id);
                                setIsFormOpen(true);
                              }}
                              style={[
                                styles.attachmentBadge,
                                (expenseAttachmentCounts[record.id] || 0) === 0 && styles.attachmentBadgeEmpty
                              ]}
                            >
                              <Paperclip size={12} color={(expenseAttachmentCounts[record.id] || 0) > 0 ? "#6b7280" : "#9ca3af"} />
                              <Text style={[
                                styles.attachmentBadgeText,
                                (expenseAttachmentCounts[record.id] || 0) === 0 && styles.attachmentBadgeTextEmpty
                              ]}>
                                {expenseAttachmentCounts[record.id] || 0}
                              </Text>
                            </Pressable>
                            <Pressable
                              onPress={() => handleDeleteExpense(record.id)}
                              style={styles.deleteButton}
                            >
                              <Trash2 size={16} color="#dc2626" />
                            </Pressable>
                          </View>
                        )}
                      </View>
                    </View>
                    {((record.type === 'expense' && record.description) || 
                      (record.type === 'maintenance' && record.description) ||
                      showServiceRequestDescription ||
                      (record.type === 'purchase' && (record.model_number || record.serial_number))) && (
                      <View style={styles.expenseCardDescription}>
                        <Text style={styles.expenseCardDescriptionText}>
                          {record.type === 'maintenance' && record.provider ? `Provider: ${record.provider} - ` : ''}
                          {record.type === 'purchase' ? (
                            <>
                              {record.model_number && `Model: ${record.model_number}`}
                              {record.model_number && record.serial_number && ' • '}
                              {record.serial_number && `S/N: ${record.serial_number}`}
                            </>
                          ) : (
                            (record.type === 'expense' || record.type === 'maintenance' || record.type === 'service_request') ? record.description : ''
                          )}
                        </Text>
                      </View>
                    )}
                    {record.type === 'service_request' && !record.is_paid && (
                      <>
                        {linkedInvoice?.status === 'open' && linkedInvoice.stripe_hosted_invoice_url ? (
                          <Pressable
                            onPress={() => handleViewInvoice(linkedInvoice.stripe_hosted_invoice_url!)}
                            style={styles.expenseInvoicePayButton}
                          >
                            <Text style={styles.invoicePayButtonText}>View & Pay Invoice</Text>
                            <ExternalLink size={16} color="#ffffff" />
                          </Pressable>
                        ) : record.invoice_requested && !record.invoice_id ? (
                          <Text style={styles.expenseInvoicePendingText}>Invoice is being prepared</Text>
                        ) : null}
                      </>
                    )}
                  </View>
                );
              })}
              <View style={styles.expenseCardTotal}>
                <Text style={styles.expenseCardTotalLabel}>Total</Text>
                <Text style={styles.expenseCardTotalAmount}>{formatCurrency(calculateTotal())}</Text>
              </View>
            </View>
          )}
        </View>
      </View>

      {/* Add Expense Modal */}
      {isFormOpen && (
        <Modal
          visible={isFormOpen}
          transparent
          animationType="slide"
          onRequestClose={() => setIsFormOpen(false)}
        >
          <View style={styles.modalOverlay}>
            <Pressable style={styles.modalBackdrop} onPress={handleCloseModal} />
            <View style={styles.modalContent}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>
                  {newExpenseId ? 'Add Files to Expense' : editingExpenseId ? 'Manage Attachments' : 'Add Expense'}
                </Text>
                <Pressable onPress={handleCloseModal}>
                  <X size={20} color="#9ca3af" />
                </Pressable>
              </View>

              <WebScrollView style={styles.modalBody}>
                {newExpenseId || editingExpenseId ? (
                    <View style={styles.fileUploadContainer}>
                    <ExpenseFileUpload
                      expenseId={newExpenseId || editingExpenseId}
                      onAttachmentsChange={async (attachments) => {
                        if (newExpenseId || editingExpenseId) {
                          const expenseId = newExpenseId || editingExpenseId!;
                          setExpenseAttachmentCounts(prev => ({
                            ...prev,
                            [expenseId]: attachments.length
                          }));
                          // Refresh the expense list to ensure UI is up to date
                          await fetchExpenses();
                        }
                      }}
                    />
                  </View>
                ) : (
                  <View style={styles.form}>
                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Title</Text>
                      <TextInput
                    value={formData.title}
                        onChangeText={(value) => setFormData({ ...formData, title: value })}
                    placeholder="Enter expense title"
                        style={styles.input}
                      />
                    </View>

                    <View style={styles.formRow}>
                      <View style={[styles.formGroup, styles.formGroupHalf]}>
                        <Text style={styles.label}>Amount</Text>
                        <View style={styles.inputWithIcon}>
                          <View style={styles.inputIconLeft}>
                            <DollarSign size={16} color="#9ca3af" />
                          </View>
                          <TextInput
                        value={formData.amount}
                            onChangeText={(value) => setFormData({ ...formData, amount: value })}
                        placeholder="0.00"
                            keyboardType="decimal-pad"
                            style={[styles.input, styles.inputWithLeftIcon]}
                          />
                        </View>
                      </View>
                      <View style={[styles.formGroup, styles.formGroupHalf]}>
                        <Text style={styles.label}>Date</Text>
                        <View style={styles.inputWithIcon}>
                          <View style={styles.inputIconLeft}>
                            <Calendar size={16} color="#9ca3af" />
                          </View>
                          <TextInput
                        value={formData.date}
                            onChangeText={(value) => setFormData({ ...formData, date: value })}
                            placeholder="YYYY-MM-DD"
                            style={[styles.input, styles.inputWithLeftIcon]}
                          />
                        </View>
                      </View>
                    </View>

                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Category</Text>
                      <View style={styles.pickerContainer}>
                        <Picker
                          selectedValue={formData.category}
                          onValueChange={(value) => setFormData({ ...formData, category: value })}
                          style={styles.picker}
                        >
                          {expenseCategories.map((category) => (
                            <Picker.Item key={category} label={category} value={category} />
                          ))}
                        </Picker>
                      </View>
                    </View>

                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Description</Text>
                      <TextInput
                    value={formData.description}
                        onChangeText={(value) => setFormData({ ...formData, description: value })}
                    placeholder="Optional description"
                        multiline
                        numberOfLines={3}
                        style={[styles.input, styles.textArea]}
                      />
                    </View>

                    

                {error && (
                      <Text style={styles.errorText}>{error}</Text>
                    )}
                  </View>
                )}
              </WebScrollView>

              <View style={styles.modalFooter}>
                <Pressable onPress={handleCloseModal} style={styles.cancelButton}>
                  <Text style={styles.cancelButtonText}>
                    {newExpenseId || editingExpenseId ? 'Done' : 'Cancel'}
                  </Text>
                </Pressable>
                {!newExpenseId && !editingExpenseId && (
                <Pressable
                  onPress={() => handleSubmit()}
                  disabled={submitting}
                  style={[styles.submitButton, submitting && styles.submitButtonDisabled]}
                >
                    {submitting ? (
                      <>
                        <ActivityIndicator size="small" color="#ffffff" />
                        <Text style={styles.submitButtonText}>Adding...</Text>
                      </>
                    ) : (
                      <Text style={styles.submitButtonText}>Add Expense</Text>
                    )}
                  </Pressable>
                )}
              </View>
            </View>
          </View>
        </Modal>
      )}

      <DropdownPicker
        visible={showHomePicker}
        onClose={() => setShowHomePicker(false)}
        options={homes.map(home => ({ 
          value: home.id, 
          label: `${home.address}, ${home.city}` 
        }))}
        selectedValue={selectedHome}
        onSelect={(homeId) => {
          setSelectedHome(homeId);
          setActiveHomeById(homeId);
        }}
      />
    </WebScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  contentContainer: {
    padding: 16,
    gap: 32,
  },
  header: {
    gap: 24,
  },
  title: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#111827',
  },
  subtitle: {
    fontSize: 16,
    color: '#6b7280',
    marginTop: 4,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 6,
    alignSelf: 'flex-start',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  addButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  statsCard: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 24,
    gap: 16,
    ...Platform.select({
      web: {
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 2,
        elevation: 1,
      },
    }),
  },
  statsCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  statsCardTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  statsCardContent: {
    gap: 16,
  },
  statItem: {
    gap: 4,
  },
  statLabel: {
    fontSize: 14,
    color: '#6b7280',
  },
  statValue: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#111827',
  },
  statCategoryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statCategoryName: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  statCategoryAmount: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    ...Platform.select({
      web: {
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 2,
        elevation: 1,
      },
    }),
  },
  cardContent: {
    padding: 24,
    gap: 24,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  cardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  homePickerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  homePicker: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    minWidth: 150,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  homePickerText: {
    fontSize: 14,
    color: '#111827',
  },
  loadingContainer: {
    padding: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyStateContainer: {
    padding: 32,
    alignItems: 'center',
    gap: 12,
  },
  emptyStateTextContainer: {
    gap: 8,
    alignItems: 'center',
  },
  emptyStateTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  emptyStateDescription: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
  },
  expensesCardList: {
    gap: 12,
  },
  expenseCard: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 16,
    gap: 12,
    ...Platform.select({
      web: {
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 2,
        elevation: 1,
      },
    }),
  },
  expenseCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  expenseCardHeaderLeft: {
    flex: 1,
    gap: 8,
    minWidth: 0,
  },
  expenseCardHeaderRight: {
    alignItems: 'flex-end',
    gap: 8,
  },
  expenseCardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  expenseCardMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  expenseCardDate: {
    fontSize: 14,
    color: '#6b7280',
  },
  expenseCardContext: {
    fontSize: 13,
    color: '#6b7280',
    fontWeight: '500',
  },
  unpaidBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: '#fef2f2',
  },
  unpaidBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#dc2626',
  },
  expenseInvoicePayButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#5469d4',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginTop: 12,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  expenseInvoicePendingText: {
    marginTop: 12,
    fontSize: 13,
    color: '#6b7280',
    fontStyle: 'italic',
  },
  expenseCardAmount: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  expenseCardActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  expenseCardDescription: {
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
  },
  expenseCardDescriptionText: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
  },
  expenseCardTotal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginTop: 4,
  },
  expenseCardTotalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  expenseCardTotalAmount: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  expensesListContainer: {
    gap: 0,
    overflow: 'hidden',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f9fafb',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  tableHeaderText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
  },
  dateColumn: {
    width: '20%',
    minWidth: 100,
  },
  titleColumn: {
    flex: 1,
    minWidth: 150,
  },
  categoryColumn: {
    width: '20%',
    minWidth: 120,
  },
  amountColumn: {
    width: '15%',
    minWidth: 100,
    alignItems: 'flex-end',
  },
  actionsColumn: {
    width: '10%',
    minWidth: 60,
    alignItems: 'center',
    justifyContent: 'center',
  },
  deleteButton: {
    padding: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  tableBody: {
    backgroundColor: '#ffffff',
  },
  tableRow: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
    ...(Platform.OS === 'web' && {
      transition: 'background-color 0.2s',
    }),
  },
  tableCellText: {
    fontSize: 14,
    color: '#111827',
  },
  tableCellTitleText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
    marginBottom: 2,
  },
  tableCellDescriptionText: {
    fontSize: 14,
    color: '#6b7280',
  },
  categoryBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  categoryBadgeText: {
    fontSize: 14,
  },
  tableCellAmountText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
    textAlign: 'right',
  },
  tableFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#f9fafb',
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  tableFooterLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  tableFooterAmount: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    maxWidth: 448,
    width: '100%',
    maxHeight: '90%',
    ...Platform.select({
      web: {
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 20 },
        shadowOpacity: 0.1,
        shadowRadius: 25,
        elevation: 10,
      },
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  modalBody: {
    flex: 1,
  },
  form: {
    padding: 24,
    gap: 16,
  },
  formGroup: {
    gap: 4,
  },
  formRow: {
    flexDirection: 'row',
    gap: 16,
  },
  formGroupHalf: {
    flex: 1,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  input: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    fontSize: 14,
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none',
    }),
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  inputWithIcon: {
    position: 'relative',
  },
  inputIconLeft: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: [{ translateY: -8 }],
    zIndex: 1,
  },
  inputWithLeftIcon: {
    paddingLeft: 36,
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    overflow: 'hidden',
  },
  picker: {
    height: Platform.OS === 'ios' ? 200 : 50,
    ...(Platform.OS === 'web' && {
      height: 40,
    }),
  },
  errorText: {
    fontSize: 14,
    color: '#dc2626',
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 12,
    padding: 24,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  cancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  cancelButtonText: {
    fontSize: 14,
    color: '#6b7280',
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  submitButtonDisabled: {
    opacity: 0.5,
  },
  submitButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  attachmentBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: '#f3f4f6',
    borderRadius: 4,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  attachmentBadgeEmpty: {
    backgroundColor: '#f9fafb',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderStyle: 'dashed',
  },
  attachmentBadgeText: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  attachmentBadgeTextEmpty: {
    color: '#9ca3af',
  },
  fileUploadContainer: {
    padding: 24,
  },
  // Invoice styles
  invoicesList: {
    gap: 16,
    width: '100%',
  },
  invoiceCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderStyle: 'solid',
    padding: 20,
    gap: 16,
    width: '100%',
    overflow: 'hidden',
    ...Platform.select({
      web: {
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 3,
        elevation: 2,
      },
    }),
  },
  invoiceCardHeader: {
    width: '100%',
  },
  invoiceCardHeaderLeft: {
    width: '100%',
    gap: 12,
  },
  invoiceHeaderTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    gap: 16,
  },
  invoiceNumberContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  invoiceNumber: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
    letterSpacing: 0.5,
  },
  invoiceBadgesContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    alignSelf: 'flex-start',
  },
  invoiceStatusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    alignSelf: 'flex-start',
    minWidth: 'auto',
    width: 'auto',
  },
  invoiceStatusText: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  invoiceOverdueBadge: {
    backgroundColor: '#fee2e2',
  },
  invoiceOverdueText: {
    color: '#dc2626',
  },
  invoiceAmountContainer: {
    alignItems: 'flex-end',
    flexShrink: 0,
  },
  invoiceAmount: {
    fontSize: 24,
    fontWeight: '700',
    color: '#111827',
    letterSpacing: -0.5,
  },
  invoiceLineItems: {
    gap: 3,
    paddingTop: 2,
  },
  invoiceLineItemsLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  invoiceLineItemText: {
    fontSize: 13,
    color: '#374151',
    fontWeight: '500',
  },
  invoiceDivider: {
    height: 1,
    backgroundColor: '#f3f4f6',
    marginVertical: 4,
  },
  invoiceDetails: {
    gap: 12,
    paddingVertical: 4,
  },
  invoiceDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'nowrap',
    ...(Platform.OS === 'web' && {
      display: 'flex',
    }),
  },
  invoiceDetailIconContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'nowrap',
    flexShrink: 0,
    ...(Platform.OS === 'web' && {
      display: 'flex',
    }),
  },
  invoiceDetailLabel: {
    fontSize: 14,
    color: '#6b7280',
    fontWeight: '500',
    flexShrink: 0,
  },
  invoiceDetailLabelOverdue: {
    color: '#dc2626',
  },
  invoiceDetailValue: {
    fontSize: 14,
    color: '#111827',
    fontWeight: '600',
    flexShrink: 0,
    textAlign: 'right',
  },
  invoiceDetailValueOverdue: {
    color: '#dc2626',
    fontWeight: '700',
    flexShrink: 0,
    textAlign: 'right',
  },
  invoiceDetailValuePaid: {
    fontSize: 14,
    color: '#16a34a',
    fontWeight: '700',
    flexShrink: 0,
    textAlign: 'right',
  },
  invoicePayButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    backgroundColor: '#5469d4',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
    marginTop: 4,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  invoicePayButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
    letterSpacing: 0.3,
  },
  invoiceDownloadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    backgroundColor: '#f9fafb',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
    marginTop: 4,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  invoiceDownloadButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111827',
    letterSpacing: 0.3,
  },
});

const pickerStyles = StyleSheet.create({
  pickerModalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  pickerModalBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  pickerModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '60%',
    ...Platform.select({
      web: {
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.1,
        shadowRadius: 10,
        elevation: 5,
      },
    }),
  },
  pickerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  pickerHeaderText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  pickerOptions: {
    flex: 1,
  },
  pickerOption: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  pickerOptionSelected: {
    backgroundColor: '#f9fafb',
  },
  pickerOptionText: {
    fontSize: 16,
    color: '#111827',
  },
  pickerOptionTextSelected: {
    fontWeight: '600',
    color: '#000000',
  },
});

export default Expenses;
